import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSEO } from '../lib/useSEO';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { studyPathAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, GraduationCap, Clock, CheckCircle, XCircle, 
  SkipForward, BookOpen, Target, TrendingUp, Zap, Home
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

export default function StudyPathPlay() {
  const { sessionId } = useParams();
  useSEO('Study Session | JepPrep', 'Active study session with targeted trivia questions for focused improvement.');
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [timer, setTimer] = useState(0);
  const [results, setResults] = useState(null);
  
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    setTimer(0);
    
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimer(Date.now() - startTimeRef.current);
    }, 100);
  }, []);

  const loadSession = useCallback(async () => {
    try {
      const response = await studyPathAPI.getSession(sessionId);
      const data = response.data;
      setSession(data);
      
      if (data.status === 'completed') {
        setResults(data.stats);
      } else {
        // Set current question
        const currentIdx = data.current_index;
        if (currentIdx < data.clues.length) {
          setCurrentQuestion({
            index: currentIdx,
            ...data.clues[currentIdx]
          });
          startTimer();
        }
      }
    } catch (error) {
      toast.error('Failed to load study session');
      console.error(error);
      navigate('/study-path');
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate, startTimer]);

  useEffect(() => {
    loadSession();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loadSession]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!answer.trim() || submitting || feedback) return;

    setSubmitting(true);
    const timeTaken = Date.now() - startTimeRef.current;
    
    if (timerRef.current) clearInterval(timerRef.current);

    try {
      const response = await studyPathAPI.answer(sessionId, {
        question_index: currentQuestion.index,
        answer: answer.trim(),
        time_taken_ms: timeTaken
      });

      const data = response.data;
      setFeedback({
        isCorrect: data.is_correct,
        correctAnswer: data.correct_answer,
        isReview: data.is_review
      });

      if (data.complete) {
        // Session complete - show results
        setTimeout(() => {
          setResults(data.stats);
        }, 2000);
      } else {
        // Move to next question after delay
        setTimeout(() => {
          setCurrentQuestion({
            index: data.next_question.index,
            question: data.next_question.question,
            category: data.next_question.category,
            value: data.next_question.value,
            is_review: data.next_question.is_review
          });
          setAnswer('');
          setFeedback(null);
          startTimer();
          inputRef.current?.focus();
        }, 2000);
      }
    } catch (error) {
      toast.error('Failed to submit answer');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (submitting || feedback) return;
    
    // Submit empty answer (skip)
    setAnswer('');
    await handleSubmit();
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const tenths = Math.floor((ms % 1000) / 100);
    return `${seconds}.${tenths}s`;
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'text-emerald-400';
    if (accuracy >= 60) return 'text-yellow-300';
    if (accuracy >= 40) return 'text-amber-400';
    return 'text-rose-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-white text-xl">Loading session...</div>
      </div>
    );
  }

  // Results Screen
  if (results) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        {/* Header */}
        <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
          <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <img src={LOGO_URL} alt="JepPrep" className="h-8 sm:h-12" />
              <h1 className="text-base sm:text-xl font-display text-white">Study Path Complete</h1>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] p-4 sm:p-8 text-center">
            <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-purple-500/20 flex items-center justify-center">
              <GraduationCap className="w-7 h-7 sm:w-10 sm:h-10 text-purple-400" />
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Practice Complete!</h2>
            <p className="text-gray-400 mb-6 sm:mb-8">{session?.category}</p>

            {/* Score Display */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8 max-w-md mx-auto">
              <div className="p-2 sm:p-4 rounded-lg bg-[hsl(var(--accent))]">
                <p className={`text-xl sm:text-3xl font-bold ${getAccuracyColor(results.accuracy)}`}>
                  {results.accuracy}%
                </p>
                <p className="text-gray-400 text-xs sm:text-sm">Accuracy</p>
              </div>
              <div className="p-2 sm:p-4 rounded-lg bg-[hsl(var(--accent))]">
                <p className="text-xl sm:text-3xl font-bold text-emerald-400">{results.correct_count}</p>
                <p className="text-gray-400 text-xs sm:text-sm">Correct</p>
              </div>
              <div className="p-2 sm:p-4 rounded-lg bg-[hsl(var(--accent))]">
                <p className="text-xl sm:text-3xl font-bold text-white">{results.total_questions}</p>
                <p className="text-gray-400 text-xs sm:text-sm">Total</p>
              </div>
            </div>

            {/* Review vs New Breakdown */}
            <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-6 sm:mb-8 max-w-md mx-auto">
              {results.review_questions?.total > 0 && (
                <div className="p-3 sm:p-4 rounded-lg bg-[hsl(var(--accent))] border border-[hsl(var(--border))]">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
                    <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
                    <span className="text-white text-xs sm:text-sm font-medium">Review</span>
                  </div>
                  <p className={`text-lg sm:text-xl font-bold ${getAccuracyColor(results.review_questions.accuracy)}`}>
                    {results.review_questions.correct}/{results.review_questions.total}
                  </p>
                  <p className="text-gray-400 text-xs">{results.review_questions.accuracy}%</p>
                </div>
              )}
              {results.new_questions?.total > 0 && (
                <div className="p-3 sm:p-4 rounded-lg bg-[hsl(var(--accent))] border border-[hsl(var(--border))]">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
                    <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-400" />
                    <span className="text-white text-xs sm:text-sm font-medium">New</span>
                  </div>
                  <p className={`text-lg sm:text-xl font-bold ${getAccuracyColor(results.new_questions.accuracy)}`}>
                    {results.new_questions.correct}/{results.new_questions.total}
                  </p>
                  <p className="text-gray-400 text-xs">{results.new_questions.accuracy}%</p>
                </div>
              )}
            </div>

            {/* Improvement Indicator */}
            <div className="mb-8">
              {results.improvement_indicator === 'improving' && (
                <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Great progress on review questions!
                </Badge>
              )}
              {results.improvement_indicator === 'stable' && (
                <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  <Target className="w-3 h-3 mr-1" />
                  Solid performance!
                </Badge>
              )}
              {results.improvement_indicator === 'needs_practice' && (
                <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  <BookOpen className="w-3 h-3 mr-1" />
                  Keep practicing - you'll improve!
                </Badge>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/study-path">
                <Button 
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="practice-again"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Practice Again
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="border-[hsl(var(--border))] text-white">
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  // Gameplay Screen
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-2 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/study-path">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-7 sm:h-12 shrink-0" />
              <span className="text-sm sm:text-xl font-display text-white hidden sm:inline">Study Path</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <span className="text-gray-400 text-xs sm:text-base">
                <span className="hidden sm:inline">Question </span>{(currentQuestion?.index || 0) + 1}/{session?.clues?.length || 0}
              </span>
              <div className="flex items-center gap-1 sm:gap-2 text-[hsl(var(--brand-gold-400))]">
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="font-mono text-xs sm:text-base">{formatTime(timer)}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-[hsl(var(--secondary))]">
        <Progress 
          value={((currentQuestion?.index || 0) / (session?.clues?.length || 1)) * 100} 
          className="h-1 rounded-none"
        />
      </div>

      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-4 sm:p-8">
            {/* Category and Value */}
            <div className="flex items-center justify-between gap-2 mb-4 sm:mb-6">
              <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs sm:text-sm truncate max-w-[50%]">
                {currentQuestion?.category || session?.category}
              </Badge>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                {currentQuestion?.is_review && (
                  <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs">
                    <BookOpen className="w-3 h-3 mr-0.5 sm:mr-1" />
                    Review
                  </Badge>
                )}
                <Badge className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] text-xs sm:text-sm">
                  ${currentQuestion?.value || 200}
                </Badge>
              </div>
            </div>

            {/* Question */}
            <div className="text-center mb-5 sm:mb-8">
              <p className="text-lg sm:text-2xl text-white leading-relaxed" data-testid="study-question">
                {currentQuestion?.question}
              </p>
            </div>

            {/* Feedback */}
            {feedback && (
              <div className={`p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 text-center ${
                feedback.isCorrect 
                  ? 'bg-emerald-500/20 border border-emerald-500/30' 
                  : 'bg-rose-500/20 border border-rose-500/30'
              }`}>
                <div className="flex items-center justify-center gap-2 mb-1 sm:mb-2">
                  {feedback.isCorrect ? (
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
                  ) : (
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400" />
                  )}
                  <span className={`text-base sm:text-lg font-bold ${feedback.isCorrect ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {feedback.isCorrect ? 'Correct!' : 'Incorrect'}
                  </span>
                </div>
                {!feedback.isCorrect && (
                  <p className="text-gray-300 text-sm sm:text-base">
                    Correct answer: <span className="text-white font-medium">{feedback.correctAnswer}</span>
                  </p>
                )}
              </div>
            )}

            {/* Answer Input */}
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <Input
                  ref={inputRef}
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="What is..."
                  disabled={!!feedback || submitting}
                  className="flex-1 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-base sm:text-lg py-5 sm:py-6"
                  data-testid="study-answer-input"
                  autoFocus
                />
                <Button
                  type="submit"
                  disabled={!answer.trim() || !!feedback || submitting}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-5 sm:py-6"
                  data-testid="study-submit-answer"
                >
                  Submit
                </Button>
              </div>
            </form>

            {/* Skip Button */}
            {!feedback && (
              <div className="mt-3 sm:mt-4 text-center">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={submitting}
                  className="text-gray-400 hover:text-white text-sm"
                  data-testid="study-skip-button"
                >
                  <SkipForward className="w-4 h-4 mr-2" />
                  Skip
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
